import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FeatureFlagContext = createContext({
  disabledKeys: [],
  disabledRoutes: [],
  disabledNavKeys: [],
  refresh: () => {},
  isNavHidden: () => false,
  isRouteBlocked: () => false,
});

/** Provides the current user's effective feature-flag state.
 *
 * Default (no user) → nothing disabled. On login, fetches
 * /api/feature-flags/effective which returns only the DISABLED keys/routes/nav
 * so the payload stays tiny (default is ON for everything).
 */
export function FeatureFlagProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState({
    disabledKeys: [], disabledRoutes: [], disabledNavKeys: [],
  });

  const refresh = useCallback(async () => {
    if (!user) {
      setState({ disabledKeys: [], disabledRoutes: [], disabledNavKeys: [] });
      return;
    }
    try {
      const { data } = await api.get("/feature-flags/effective");
      setState({
        disabledKeys: data.disabled_keys || [],
        disabledRoutes: data.disabled_routes || [],
        disabledNavKeys: data.disabled_nav_keys || [],
      });
    } catch (e) {
      // If it fails (e.g. server down), default to everything ON.
      setState({ disabledKeys: [], disabledRoutes: [], disabledNavKeys: [] });
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const isNavHidden = (path) =>
    state.disabledNavKeys.includes(path);

  const isRouteBlocked = (pathname) =>
    state.disabledRoutes.some((prefix) =>
      pathname === prefix || pathname.startsWith(prefix + "/")
    );

  return (
    <FeatureFlagContext.Provider value={{ ...state, refresh, isNavHidden, isRouteBlocked }}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

export const useFeatureFlags = () => useContext(FeatureFlagContext);
